import { create } from 'zustand'

import { getShellActiveTabId } from '@/components/layout/shellActiveTab'
import type { SaveResult } from '@/hooks/useEditorDraft'
import { useTabDrafts } from '@/stores/tabDrafts'

/**
 * One editor inside a composite tab. `isDirty` and `save` are read through a
 * stable proxy, so an inline entry object costs nothing per render.
 *
 * Convention for the cards still to be wired: `isDirty` RETURNS a boolean the
 * card already computed for its own render (`() => isDirty`) — it never
 * computes one. The thunk is called from outside React, per entry, on every
 * recompute, so it must stay free.
 */
export type EditorEntry = {
  /** Human-readable editor name; used in the joined failure message. */
  label: string
  isDirty: () => boolean
  save: () => Promise<SaveResult>
}

interface EditorRegistryState {
  /** tabId -> the editors mounted in that tab right now. */
  entries: Map<string, Set<EditorEntry>>
  register: (tabId: string, entry: EditorEntry) => void
  unregister: (tabId: string, entry: EditorEntry) => void
  /**
   * Saves every dirty editor in the tab concurrently. Partial failures are
   * reported by card label; one failure fails the whole call, because the
   * unsaved cards are still the only copy of that work.
   */
  saveAllDirty: (tabId: string) => Promise<SaveResult>
}

/**
 * Every editor of every composite tab, keyed by tab id.
 *
 * The point: a composite page (a duty, an eval case) is ~10 independent
 * editors, and the close coordinator holds exactly ONE owner per tab.
 * `CompositeTabScope` registers that owner and points it here.
 *
 * Same immutability contract as closeOwners/tabDrafts: every mutation replaces
 * the Map (and the Set inside it) so selector subscribers see a referential
 * change.
 */
export const useEditorRegistry = create<EditorRegistryState>()((set, get) => ({
  entries: new Map(),

  register: (tabId, entry) => {
    if (!tabId) return
    set((s) => {
      const next = new Map(s.entries)
      next.set(tabId, new Set(s.entries.get(tabId)).add(entry))
      return { entries: next }
    })
    // Always recompute: re-showing a backgrounded tab re-registers its cards,
    // which is what heals a marker left stale by the unregister rule below.
    publishTabDirty(tabId)
  },

  unregister: (tabId, entry) => {
    if (!tabId) return
    set((s) => {
      const current = s.entries.get(tabId)
      if (!current?.has(entry)) return s
      const remaining = new Set(current)
      remaining.delete(entry)
      const next = new Map(s.entries)
      if (remaining.size === 0) next.delete(tabId)
      else next.set(tabId, remaining)
      return { entries: next }
    })
    // Active-aware, because an editor leaves the tree for two very different
    // reasons and they are otherwise indistinguishable from here:
    //
    //  * the tab is VISIBLE — a sub-view swap or a real card unmount. The card
    //    is gone, its work with it, so the marker must follow: leaving it set
    //    would prompt for work no editor can save (a Save that saves nothing).
    //  * the tab is NOT the active one — <Activity> is tearing down a
    //    backgrounded tab's effects. The entry set dies, the hidden tree and
    //    its unsaved state do not, so the marker stays. Stale-dirty costs one
    //    extra prompt; stale-clean silently discards the work.
    if (getShellActiveTabId() === tabId) publishTabDirty(tabId)
  },

  saveAllDirty: async (tabId) => {
    const entries = get().entries.get(tabId)
    // Marked dirty with no editor registered (cards still behind Suspense):
    // nothing here can save, and {ok:true} would close over the unsaved work.
    if (!entries && useTabDrafts.getState().isDirty(tabId))
      return { ok: false, message: 'Editors are still loading. Try again.' }
    const dirty = [...(entries ?? [])].filter((entry) => entry.isDirty())
    const results = await Promise.allSettled(dirty.map((entry) => entry.save()))
    const failures = results.flatMap((result, i) => {
      const label = dirty[i]!.label
      if (result.status === 'rejected') {
        const reason: unknown = result.reason
        const message =
          reason instanceof Error ? reason.message : 'Request failed'
        return [`${label} — ${message}`]
      }
      return result.value.ok ? [] : [`${label} — ${result.value.message}`]
    })
    // No publish here: each saved card re-renders clean and publishes its own
    // edge, and the close that follows a full success clears the flag outright.
    return failures.length === 0
      ? { ok: true }
      : { ok: false, message: `Failed to save: ${failures.join('; ')}` }
  },
}))

/**
 * Recomputes the tab's aggregate dirtiness from its live entries and mirrors it
 * into the tab-drafts flag — the one boolean that lights the shelf's dirty dot,
 * arms the close prompt, and arms the unload guard.
 */
export function publishTabDirty(tabId: string): void {
  if (!tabId) return
  const entries = useEditorRegistry.getState().entries.get(tabId)
  const dirty = entries ? [...entries].some((entry) => entry.isDirty()) : false
  useTabDrafts.getState().setDirtyFlag(tabId, dirty)
}
